import React, { ReactNode, useCallback, useEffect } from "react";
import { GlobalStyles } from "@mui/material";
import {
  AnalyticsService,
  AnalyticsTrackingEvent,
  BRANDCLUB_BRAND_ID,
  setOtpAuth,
  useFingerprintJSContext,
} from "@brandclub/common-ui";
import { styled } from "@mui/material/styles";
import { AnimatePresence } from "motion/react";
import CookiesProvider from "react-cookie/cjs/CookiesProvider";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
} from "react-router-dom";
import { LocationTrackingContextProvider } from "./providers/LocationTrackingProvider";

import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { reloadProfile } from "../redux/actions/userProfile";
import { useTrackViewEvent } from "../utils/hooks/useTracking";
import { updateVisitorTracking } from "../redux/reducers/visitorTracking";
import { StoreBrandingType } from "../types/misc";
import { removeFunctionFields } from "../utils/misc";
import ScrollToTop from "../utils/routing/ScrollToTop";
import { SiteProvider } from "./SiteProvider";
import AppDialogs from "./StoreComponents/StoreDialogs";
import StoreFullScreenLoading from "./StoreComponents/StoreFullScreenLoading";
import StoreSinglePageOutlet from "./StoreComponents/StoreSinglePageOutlet";
import { SyncProvider } from "./SyncProvider";
import AppMainPageOutlet from "./pages/AppPages/AppMainPageOutlet";
import AuthSignUpPageOutlet from "./pages/Auth/AuthSignUpPageOutlet";
import UserLoginProvider from "./pages/Auth/UserLoginProvider";
import { AuthHelper } from "./pages/Auth/AuthHelper";
import { formatPhone } from "../utils/StringUtils";
import {
  appPageRoutes,
  brandstoreLandingRoutes,
  dtcCommonAppPageRoutes,
  dtcConciergeRoutes,
  fallbackRoutes,
} from "./routes/appPageRoutes";
import { authRoutes } from "./routes/authRoutes";
import { dashboardRoutes, dtcDashboardRoutes } from "./routes/dashboardRoutes";
import { appGeneralRoutes, generalRoutes } from "./routes/generalRoutes";
import { buildRoutes } from "./routes/routeBuilder";
import {
  checkoutRoutes,
  dtcCheckoutRoutes,
  dtcSinglePageRoutes,
  membershipProgramRoutes,
  singlePageCustomCloseRoutes,
  singlePageRoutes,
} from "./routes/singlePageRoutes";
import {
  connectRoutes,
  syncRoutes,
  syncStatusRoutes,
} from "./routes/syncRoutes";
import DtcFooterOutlet from "./pages/AppPages/DtcFooterOutlet";
import { useCustomTheme } from "../utils/hooks/useCustomTheme";
import { AppSuspense } from "./AppSuspense";
import { ConciergeContextProvider } from "@brandclub/common-ui";
import {
  ConciergeFloatingButtonAdapter,
  ConciergeWidgetAdapter,
} from "./pages/AppPages/Concierge/ConciergeWidgetAdapter";
import { ThemeManager } from "@brandclub/bc-widgets";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "../queryClient";

const StyledMain = styled("main")(() => ({
  overflow: "hidden",
}));

const RouteProviders = () => {
  useCustomTheme();
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const retailers = useAppSelector(({ retailers }) => retailers);
  const isSignedIn = !!userProfile?.userId;

  const retailerIdToNameMap = React.useMemo(() => {
    const map: Record<number, string> = {};
    retailers?.forEach((r) => {
      map[r.retailerId] = r.displayName;
    });
    retailers?.forEach((r) => {
      if (r.parentRetailerId && !(r.parentRetailerId in map)) {
        map[r.parentRetailerId] = r.displayName;
      }
    });
    return map;
  }, [retailers]);

  // Inline sign-in: formats the phone number, signs up (if new) and sends an OTP via SMS.
  const inlineSignIn = useCallback(
    async (rawPhone: string): Promise<{ session: any; isNewUser: boolean } | { error: string }> => {
      const phoneNumber = formatPhone(rawPhone, "e164");
      try {
        await AuthHelper.signUp(phoneNumber);
        const session = await AuthHelper.sendAuthOtpWithSms(phoneNumber, true);
        return { session, isNewUser: true };
      } catch (e: any) {
        if (e.code === "UsernameExistsException") {
          try {
            const session = await AuthHelper.sendAuthOtpWithSms(phoneNumber, false);
            return { session, isNewUser: false };
          } catch {
            return { error: "An error occurred. Please try again." };
          }
        }
        return { error: "An error occurred. Please try again." };
      }
    },
    []
  );

  // Inline OTP verification: answers the Cognito custom challenge, sets the auth session,
  // and reloads the Redux user profile so isSignedIn flips to true in the app.
  const inlineVerifyOtp = useCallback(
    async (
      session: any,
      otp: string
    ): Promise<{ success: true } | { success: false; error: string }> => {
      try {
        const cognitoUser = await AuthHelper.answerCustomChallenge(session, otp);
        await setOtpAuth(cognitoUser);
        await dispatch(reloadProfile());
        return { success: true };
      } catch (e: any) {
        return {
          success: false,
          error: e.message || "Invalid code, please try again",
        };
      }
    },
    [dispatch]
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeManager />
      <ConciergeContextProvider isSignedIn={isSignedIn} retailerIdToNameMap={retailerIdToNameMap} inlineSignIn={inlineSignIn} inlineVerifyOtp={inlineVerifyOtp}>
        <ScrollToTop />
        <GlobalStyles
          styles={(theme) => ({
            body: {
              fontFamily: `${theme.typography.fontFamily}`,
            },
          })}
        />
        <LocationTrackingContextProvider>
          <TrackPageViewContextProvider>
            <CookiesProvider>
              <UserLoginProvider>
                <SiteProvider>
                  <SyncProvider>
                    <AppSuspense fallback={<StoreFullScreenLoading />}>
                      <AppDialogs />
                      <Outlet />
                      <ConciergeFloatingButtonAdapter />
                      <ConciergeWidgetAdapter />
                    </AppSuspense>
                  </SyncProvider>
                </SiteProvider>
              </UserLoginProvider>
            </CookiesProvider>
          </TrackPageViewContextProvider>
        </LocationTrackingContextProvider>
      </ConciergeContextProvider>
    </QueryClientProvider>
  );
};

const UniversalStoreRoutes = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route element={<RouteProviders />}>
        {buildRoutes(generalRoutes)}
        {buildRoutes(fallbackRoutes)}
        <Route element={<StoreSinglePageOutlet />}>
          <Route element={<AuthSignUpPageOutlet />}>
            <Route>{buildRoutes(authRoutes)}</Route>
          </Route>
          {buildRoutes(singlePageCustomCloseRoutes)}
          {buildRoutes(singlePageRoutes)}
          {buildRoutes(checkoutRoutes)}
        </Route>

        <Route element={<AppMainPageOutlet />}>
          {/*app pages */}
          {buildRoutes(brandstoreLandingRoutes)}
          {buildRoutes(appGeneralRoutes)}
          {buildRoutes(appPageRoutes)}
          {buildRoutes(syncRoutes)}
          {buildRoutes(syncStatusRoutes)}
          {buildRoutes(dashboardRoutes)}
        </Route>
      </Route>
    </Route>
  )
);

const DtcRoutes = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route element={<RouteProviders />}>
        {buildRoutes(generalRoutes)}
        {buildRoutes(fallbackRoutes)}
        <Route element={<StoreSinglePageOutlet />}>
          <Route element={<DtcFooterOutlet />}>
            {buildRoutes(dtcDashboardRoutes)}
            {buildRoutes(dtcCommonAppPageRoutes)}
            {buildRoutes(syncRoutes)}
            {buildRoutes(syncStatusRoutes)}
            {buildRoutes(connectRoutes)}
            {buildRoutes(membershipProgramRoutes)}
          </Route>
          {buildRoutes(dtcConciergeRoutes)}
          <Route element={<AuthSignUpPageOutlet />}>
            <Route>{buildRoutes(authRoutes)}</Route>
          </Route>
          {buildRoutes(dtcCheckoutRoutes)}
          {buildRoutes(dtcSinglePageRoutes)}
        </Route>
      </Route>
    </Route>
  )
);

const TrackPageViewContext = React.createContext<
  (data: { pageType: string; pageId: string }) => void
>(() => {});

export const useAnalyticsTrackPageView = (
  pageType?: string,
  pageId?: string,
  additionalData?: any
) => {
  const trackPageView = React.useContext(TrackPageViewContext);
  return () => {
    trackPageView({ pageType, pageId, ...additionalData });
  };
};
const TrackPageViewContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const trackPageView = useTrackViewEvent<{ pageType: string; pageId: string }>(
    AnalyticsTrackingEvent.PAGE_VIEW
  );
  return (
    <TrackPageViewContext.Provider value={trackPageView}>
      {children}
    </TrackPageViewContext.Provider>
  );
};

const RoutesSwitch = () => {
  const appConfig = useAppSelector(({ appConfig }) => appConfig);
  if (
    appConfig?.domainConfig.brandId !== BRANDCLUB_BRAND_ID &&
    appConfig?.domainConfig.storeBrandingType ===
      StoreBrandingType.CustomDTCStore
  ) {
    return (
      <AnimatePresence mode="wait">
        <RouterProvider router={DtcRoutes} />
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <RouterProvider router={UniversalStoreRoutes} />
    </AnimatePresence>
  );
};

const AppRoutes = () => {
  const visitorTracking = useFingerprintJSContext();
  const userProfile = useAppSelector((state) => state.userProfile);
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Mixpanel tracking
    try {
      if (!AnalyticsService.isMixpanelInitialized()) {
        AnalyticsService.initializeMixpanel();
      }
      AnalyticsService.setCurrentVisitorTracking(visitorTracking);
      AnalyticsService.setMixpanelUserIdentity(visitorTracking, userProfile!);
      if (visitorTracking?.uniqueVisitorId) {
        dispatch(updateVisitorTracking(removeFunctionFields(visitorTracking)));
      }
    } catch (e) {
      console.warn(e);
    }
  }, [dispatch, visitorTracking, userProfile]);

  return (
    <StyledMain>
      <RoutesSwitch />
    </StyledMain>
  );
};

export default AppRoutes;
